import { App, Button, Flex, Input, Skeleton, Space } from "antd"
import styled, { useTheme } from "styled-components"
import { OrderInfoInterface, PriceInterface } from "../../types"

import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { useQuery } from "@tanstack/react-query"
import { useContext, useEffect, useState } from "react"
import { ShowMessagePage } from "../../config"
import { UserContext } from "../../context"

import { useNavigate } from "react-router-dom"
import { getPaymentIntent, getProducts, getPromotionDetails, submitOrder } from "../../endpoints"
import { LabeledBox } from "../GeneralComponents"

const SummaryTable = styled.table`
    table-layout: fixed;
    width: 100%;
`
const STD1 = styled.td`
    /* width: 12em; */
    overflow: hidden;
`
const STD2 = styled.td`
    /* width: 20em; */
    overflow: hidden;
    word-wrap: break-word;
`

function trimAddressFields(address: any) {
    if (!address) return address
    const trimmed: any = {}
    for (const key in address) {
        const value = address[key]
        trimmed[key] = typeof value === "string" ? value.trim() : value
    }
    return trimmed
}

export const OrderSummary = (props: {
    totalPrice: PriceInterface | null
    orderInfo: OrderInfoInterface,
    
    setDiscountedPrice: React.Dispatch<React.SetStateAction<number | null>>
    setOrderInfo: React.Dispatch<React.SetStateAction<OrderInfoInterface>>
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>
}): JSX.Element => {
    const [needTemplate, setNeedTemplate] = useState<boolean>(false)
    const { modal } = App.useApp()
    const navigate = useNavigate()
    const user = useContext(UserContext)
    const stripe = useStripe() // Use Stripe object
    const elements = useElements() // Use Stripe elements
    const [priceReduction, setPriceReduction] = useState<string>("")
    const [promotionCode, setPromotionCode] = useState<string>("")
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [loading, setLoading] = useState(false)
    const [validSequenceInput,setValidSequenceInput]=useState<string>('')
    useEffect(() => {
        if (props.orderInfo.productid===7) {
            let hasPolyA=true
            for(const sequence of props.orderInfo.samples?.map((x)=>x.sequence)??[]){
                if(!sequence ||!sequence.toLocaleUpperCase().includes("AAAAAAAAAAAAAAAAAA")){
                    hasPolyA=false
                    break
                }
            }
            if(!hasPolyA){
                setValidSequenceInput("Error: some template sequence does not have polyA.")
            }
        }
    }, [props.orderInfo.sample_sequences,props.orderInfo.productid])
    useEffect(() => {
        if (!props.orderInfo.samples?.length) {
            setErrorMessage("")
        }
        const names = props.orderInfo.samples?.map((x) => x.sampleName) ?? []
        const illegalCharacters = names.join("").match(/[^a-zA-Z0-9\-_\n.]/g)
        if (illegalCharacters) {
            setErrorMessage(
                "sorry, " +
                    illegalCharacters +
                    (illegalCharacters.length > 1 ? " are" : " is") +
                    " not allowed in sample names"
            )
        } else if (new Set(names).size !== names.length) {
            setErrorMessage("Repeated sample names detected")
        } else {
            const sequences = props.orderInfo.samples?.map((x) => x.sequence) ?? []
            const illegalBases = sequences.join("").match(/[^agctAGCT]/g)
            if (illegalBases) {
                setErrorMessage(
                    illegalBases + (illegalBases.length > 1 ? " are" : " is") + " not allowed in DNA sequences"
                )
            } else {
                setErrorMessage("")
            }
        }
    }, [props.orderInfo.samples, props.orderInfo.samples])
    const handleSubmit = async () => {
        if (loading) return // Prevent double submit
        setLoading(true)
        try {
            // console.log("submit order")
            // consolelog("props.orderInfo is", props.orderInfo)
            if (errorMessage) {
                modal.error({
                    content: "Please correct the error in the fasta input",
                })
                return
            }

            if (!props.orderInfo.shippinginfo?.method) {
                modal.error({
                    content: "Please enter shipping information",
                })
                return
            }
            if (props.orderInfo.shippinginfo?.method === "pickup") {
                const pickupInfo = props.orderInfo.shippinginfo.pickupinfo
                if (!pickupInfo?.line1 || !pickupInfo?.city || !pickupInfo?.state) {
                    modal.error({
                        content: "Please enter address information for sample pick up",
                    })
                    return
                }
            }
            if (props.orderInfo.shippinginfo?.method === "dropbox") {
                if (!props.orderInfo.shippinginfo.dropbox?.id) {
                    modal.error({ content: "Please choose dropbox" })
                    return
                }
            }

            if (props.orderInfo.samples.length <= 0) {
                modal.error({ content: "No samples entered" })
                return
            }
            if ((!props.orderInfo.paymentInfo ||(props.orderInfo?.paymentInfo?.method === "PO" && !props.orderInfo?.paymentInfo?.PoNumber)) && props.orderInfo.totalPrice?.price && props.orderInfo.totalPrice.price > 0) {
                modal.error({
                    content: "Please enter payment information",
                })
                return
            }
            // console.log("order info is", props.orderInfo)
            // console.log('props.totalPrice is', props.totalPrice)
            if((props.totalPrice?.price??0)<=0){
                console.log("total price is 0, no payment needed",props.totalPrice?.price,)
                let cleanedOrderInfo = { ...props.orderInfo }
                if (cleanedOrderInfo.shippinginfo?.pickupinfo) {
                    cleanedOrderInfo = {
                        ...cleanedOrderInfo,
                        shippinginfo: {
                            ...cleanedOrderInfo.shippinginfo,
                            pickupinfo: trimAddressFields(cleanedOrderInfo.shippinginfo.pickupinfo),
                        },
                    }
                }

                const orderInfo = {
                    ...cleanedOrderInfo,
                    productDetails: data?.products.find((x) => x.id === cleanedOrderInfo.productid),
                    totalPrice: props.totalPrice ?? undefined,
                    promotionCode: promotionCode,
                }
                // consolelog("orderinfo is", orderInfo)
                const result = await submitOrder({ orderInfo })
                if (result.code === 1000) {
                    // Instead of setShowConfirmation(true), do:
                    navigate("/order/confirmation", { state: { orderId: result.orderId } })
                    return
                } else {
                    console.log(result)
                    modal.error({ content: "Order submission failed: " + result.message })
                    return
                }
            }else if(props.orderInfo.paymentInfo){
                // console.log("payment info is", props.orderInfo.paymentInfo)
                if (props.orderInfo.paymentInfo?.method === "PO") {
                    // console.log("PO payment, payment info is", props.orderInfo.paymentInfo)
                    if (!props.orderInfo.paymentInfo.PoNumber) {
                        modal.error({ content: "Please enter PO number" })
                        return
                    } else {
                        // Helper to trim all string fields in shippinginfo.pickupinfo

                        // Clean up order info
                        let cleanedOrderInfo = { ...props.orderInfo }
                        if (cleanedOrderInfo.shippinginfo?.pickupinfo) {
                            cleanedOrderInfo = {
                                ...cleanedOrderInfo,
                                shippinginfo: {
                                    ...cleanedOrderInfo.shippinginfo,
                                    pickupinfo: trimAddressFields(cleanedOrderInfo.shippinginfo.pickupinfo),
                                },
                            }
                        }

                        const orderInfo = {
                            ...cleanedOrderInfo,
                            productDetails: data?.products.find((x) => x.id === cleanedOrderInfo.productid),
                            totalPrice: props.totalPrice ?? undefined,
                            promotionCode: promotionCode,
                        }
                        // consolelog("orderinfo is", orderInfo)
                        const result = await submitOrder({ orderInfo })
                        if (result.code === 1000) {
                            // Instead of setShowConfirmation(true), do:
                            navigate("/order/confirmation", { state: { orderId: result.orderId } })
                            return
                        } else {
                            console.log(result)
                            modal.error({ content: "Order submission failed: " + result.message })
                            return
                        }
                    }
                } else if (props.orderInfo.paymentInfo?.method === "credit card") {
                    // console.log("credit payment")
                    if (!props.totalPrice || props.totalPrice.price < 0) {
                        modal.error({ content: "Total price is not calculated" })
                        return
                    } else {
                        if (!stripe || !elements) {
                            modal.error({ content: "Payment gateway not ready" })
                            return
                        }

                        const intentResult = await getPaymentIntent({
                            cents: props.totalPrice.price * 100,
                            currency: "usd",
                            metadata: {
                                firstname: user.user?.firstName ?? "",
                                lastname: user.user?.lastName ?? "",
                            },
                        })
                        if (intentResult.code === 1000) {
                            const clientSecret = intentResult.secret ?? "" //this will not be empty
                            // console.log("client secret:")
                            // console.log(clientSecret)
                            const result = await stripe.confirmCardPayment(clientSecret, {
                                payment_method: {
                                    card: elements.getElement(CardElement)!,
                                },
                            })
                            // console.log("payment result")
                            // console.log(result)
                            if (result.error) {
                                modal.error({
                                    content: "Payment failed, please try again.  Error message:" + result.error.message,
                                })
                                return
                            } else if (result.paymentIntent && result.paymentIntent.status === "succeeded") {
                                //submit the order with the paymentid
                                const orderInfo = {
                                    ...props.orderInfo,
                                    productDetails: data?.products.find((x) => x.id === props.orderInfo.productid),
                                    totalPrice: props.totalPrice ?? undefined,
                                    promotionCode: promotionCode,
                                    paymentInfo: {
                                        ...props.orderInfo.paymentInfo,
                                        creditCardPaymentId: result.paymentIntent.id,
                                    },
                                }

                                const submitresult = await submitOrder({ orderInfo })

                                if (submitresult.code === 1000) {
                                    // Instead of setShowConfirmation(true), do:
                                    navigate("/order/confirmation", { state: { orderId: submitresult.orderId } })
                                    return // Prevent further code execution
                                }
                                console.log(submitresult)
                            }
                        } else {
                            modal.warning({ content: "payment error" })
                        }
                    }
                }
            }
            
        } finally {
            setLoading(false)
        }
    }

    const theme = useTheme()

    const { isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ["getproducts"],
        queryFn: getProducts,
    })
    useEffect(() => {
        // console.log(
        //   data?.products.find((x) => x.id == props.orderInfo.productid)
        // )
        setNeedTemplate(data?.products.find((x) => x.id === props.orderInfo.productid)?.needTemplate ?? false)
    }, [data, props.orderInfo.productid])
    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error.message)
    }
    if (isSuccess) {
    }

    if (!props.totalPrice) return <></>

    return (
        <>
            {props.orderInfo.productid !== null && (
                <LabeledBox title="Order Summary" border style={{ width: "100%" }}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <div
                            style={{
                                backgroundColor: theme.background,
                                borderRadius: "7px",
                                borderStyle: "none",
                            }}
                        >
                            {!needTemplate && (
                                <ol
                                    style={{
                                        textAlign: "left",
                                        listStylePosition: "inside",
                                        listStyleType: "square",
                                    }}
                                >
                                    {props.orderInfo.samples?.map((d, i) => (
                                        <li key={i} id={i + ""} style={{ lineHeight: "1.5em" }}>
                                            {d.sampleName ?? ""}
                                        </li>
                                    ))}
                                </ol>
                            )}
                            {needTemplate && (
                                <table style={{ minWidth: "16em", width: "100%" }}>
                                    <thead>
                                        <tr style={{ textAlign: "left" }}>
                                            <th>Sample</th>
                                            <th>Template</th>

                                            <th>Size</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {props.orderInfo.samples?.map((sample, i) => {
                                            return (
                                                <tr key={i}>
                                                    <td>{sample.sampleName}</td>
                                                    <td>{sample.sequence ? "yes" : "no"}</td>
                                                    <td>
                                                        {sample.sequence
                                                            ? sample.fullLength
                                                                ? "full"
                                                                : "partial"
                                                            : ""}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            )}
                            <div style={{ color: "red" }}>{errorMessage}</div>
                        </div>
                        <SummaryTable>
                            <tbody>
                                <tr>
                                    <STD1>
                                        Service type: 
                                    </STD1>
                                    <STD2>
                                        {data?.products.find((x) => x.id === props.orderInfo.productid)?.name || "Unknown Service"}
                                    </STD2>
                                </tr>
                                <tr>
                                    <STD1>
                                        <b>Order total: </b>
                                    </STD1>
                                    <STD2>
                                        <b>${props.totalPrice.price}</b>
                                        {priceReduction && (
                                            <span style={{ marginLeft: "1rem", color: "red" }}>
                                                {"Reduced" + (priceReduction)}
                                            </span>
                                        )}
                                    </STD2>
                                </tr>
                                <tr>
                                    <STD1># of samples:</STD1>
                                    <STD2>{props.orderInfo.samples.length}</STD2>
                                </tr>
                                <tr>
                                    <STD1>Order name:</STD1>
                                    <STD2>{props.orderInfo.ordername}</STD2>
                                </tr>
                                {/* <tr>
                  <STD1>Order type: </STD1>
                  <STD2>
                    {data?.products[props.orderInfo.productid].name}
                  </STD2>
                </tr> */}
                                <tr>
                                    <STD1>Sample concentration:</STD1>
                                    <STD2>
                                        {props.orderInfo.concentration ? props.orderInfo.concentration + "ng/μl" : ""}
                                    </STD2>
                                </tr>
                                {props.orderInfo.productid === 7? (<>
                                <tr>
                                    <STD1>Linearized:</STD1>
                                    <STD2>{props.orderInfo.linearized ? "Yes" : "No"}</STD2>
                                </tr>
                                </>) : props.orderInfo.productid === 2? (<>
                                {props.orderInfo.colonies && (<><tr>
                                    <STD1>Colony sequencing:</STD1>
                                    <STD2>{props.orderInfo.colonies?"Yes":"No"}</STD2>
                                </tr></>)}
                                <tr>
                                    <STD1>ITR purity analysis:</STD1>
                                    <STD2>{props.orderInfo.itrPurityAnalysis ? "Yes" : "No"}</STD2>
                                </tr>
                                </>)
                                : props.orderInfo.productid === 6? (<>
                                <tr>
                                    <STD1>ITR purity analysis:</STD1>
                                    <STD2>{props.orderInfo.itrPurityAnalysis ? "Yes" : "No"}</STD2>
                                </tr>
                                </>)
                                :<></>}
                              <tr>
                                    <STD1>Special notes:</STD1>
                                    <STD2>{props.orderInfo.specialNotes}</STD2>
                                </tr>
                              
                            </tbody>
                        </SummaryTable>
                        <div style={{ marginTop: "1em" }}>
                            <div>Promotion code:</div>
                            <Flex gap={"0px"}>
                                <Input
                                    style={{ color: "black" }}
                                    styles={{
                                        input: { width: "12em" },
                                    }}
                                    type="text"
                                    id="promotioncode"
                                    value={props.orderInfo.promotionCode}
                                    onChange={(e) => {
                                        props.setOrderInfo({
                                            ...props.orderInfo,
                                            promotionCode: e.target.value,
                                        })
                                    }}
                                    // onChange={(e) => setEmail(e.target.value)}
                                    size="middle"
                                    name="concentration"
                                />
                                <Button
                                    onClick={async () => {
                                        if (!props.orderInfo.promotionCode) {
                                            modal.warning({ content: "please enter a promotion code" })
                                        } else {
                                            let regularUnitPrice = data?.products
                                                .find((x) => x.id === props.orderInfo.productid)
                                                ?.price.find((x) => x.country === "USA")
                                                ?.price ?? 0
                                            if(props.orderInfo.colonies){
                                                regularUnitPrice +=5
                                            }
                                            const priceBeforePromotion = regularUnitPrice * props.orderInfo.samples.length
                                            let discoutedTotalPrice=priceBeforePromotion
                                            const result = await getPromotionDetails({
                                                code: props.orderInfo.promotionCode,
                                            })
                                            // console.log('result is',result)
                                            if (result && result.code === 1000) {
                                                const promotion = result.promotionDetails
                                                const sid = props.orderInfo.productid

                                                if (sid !== null) {
                                                    // console.log('sid is',sid)

                                                    for (const detail of promotion.details ?? []) {
                                                        // console.log('detail is', detail)
                                                        if (detail.productid === sid || detail.productid === -1) {
                                                            // console.log('matched product',detail)

                                                            if (detail.promotionType === "percent off") {
                                                                // console.log('percent off, detail is', detail)
                                                                let regularUnitPrice = data?.products
                                                                    .find((x) => x.id === sid)
                                                                    ?.price.find((x) => x.country === "USA")
                                                                    ?.price ?? 0
                                                                let discountedUnitPrice = (regularUnitPrice ?? 0) *
                                                                        (100 - (detail.percentOff ?? 0)) /
                                                                        100
                                                                if (discountedUnitPrice > 0) {
                                                                    //when discount is 0, we want to make it just zero
                                                                    
                                                                    if (props.orderInfo.colonies) {
                                                                        discountedUnitPrice += 5
                                                                    }
                                                                }
                                                                if (props.orderInfo.colonies) {
                                                                    regularUnitPrice += 5
                                                                }
                                                                if(promotion.maxSamples>-1){
                                                                    const nSamples= props.orderInfo.samples.length
                                                                    let finalPrice=0
                                                                    if (nSamples > promotion.maxSamples) {
                                                                        finalPrice = discountedUnitPrice*promotion.maxSamples + regularUnitPrice*(nSamples-promotion.maxSamples)
                                                                    }else{
                                                                        finalPrice = discountedUnitPrice*nSamples
                                                                    }

                                                                    props.setDiscountedPrice(finalPrice)
                                                                    discoutedTotalPrice = finalPrice
                                                                }else{
                                                                     props.setDiscountedPrice(
                                                                            discountedUnitPrice *
                                                                                props.orderInfo.samples.length
                                                                        )
                                                                }
                                                            } else if (detail.promotionType === "low price") {
                                                                // console.log('low price, detail is', detail)
                                                                if (detail.price) {
                                                                    let regularUnitPrice = data?.products
                                                                        .find((x) => x.id === sid)
                                                                        ?.price.find((x) => x.country === "USA")
                                                                        ?.price ?? 0
                                                                    if(props.orderInfo.colonies){
                                                                        regularUnitPrice +=5
                                                                    }
                                                                    let discountedUnitPrice = detail.price.find(
                                                                        (x) => x.country === "USA"
                                                                    )?.price ?? 0
                                                                    if(discountedUnitPrice>0){
                                                                        //when discount is 0, we want to make it just zero
                                                                        if (props.orderInfo.colonies) {
                                                                            discountedUnitPrice += 5
                                                                        }

                                                                    }
                                                                    // console.log('regularUnitPrice is', regularUnitPrice)
                                                                    // console.log('discountedUnitPrice is', discountedUnitPrice)
                                                                    // console.log('promotion is', promotion)
                                                                    if(promotion.maxSamples>-1){
                                                                        const nSamples= props.orderInfo.samples.length
                                                                        let finalPrice=0
                                                                        if (nSamples > promotion.maxSamples) {
                                                                            // console.log('goes here 121')
                                                                            finalPrice = discountedUnitPrice*promotion.maxSamples + regularUnitPrice*(nSamples-promotion.maxSamples)
                                                                        }else{
                                                                            // console.log('goes here 122')
                                                                            finalPrice = discountedUnitPrice*nSamples
                                                                        }
                                                                        // console.log('finalPrice is', finalPrice)

                                                                        props.setDiscountedPrice(finalPrice)
                                                                        discoutedTotalPrice = finalPrice
                                                                        
                                                                    }else{
                                                                        props.setDiscountedPrice(
                                                                            discountedUnitPrice *
                                                                                props.orderInfo.samples.length
                                                                        )
                                                                        discoutedTotalPrice = discountedUnitPrice *
                                                                            props.orderInfo.samples.length
                                                                    }
                                                                
                                                                }
                                                            }
                                                            props.setOrderInfo({
                                                                ...props.orderInfo,
                                                                promotionDetails: promotion,
                                                            })
                                                            setPriceReduction(""+(priceBeforePromotion-discoutedTotalPrice))
                                                            setPromotionCode(promotion.code)

                                                        }
                                                    }
                                                }
                                                props.setOrderInfo({
                                                    ...props.orderInfo,
                                                    promotionDetails: result.promotionDetails,
                                                })
                                            } else {
                                                modal.warning({ content: result.message })
                                            }
                                        }
                                    }}
                                >
                                    apply
                                </Button>
                            </Flex>
                            {validSequenceInput&&(<div style={{ color: "red" ,marginTop: "1em" }}>{validSequenceInput}</div>)}
                            <Space>
                                
                                <Button
                                    size="large"
                                    type="primary"
                                    disabled={!stripe || loading||validSequenceInput!==''}
                                    loading={loading}
                                    style={{ marginTop: "2em" }}
                                    onClick={handleSubmit}
                                >
                                    Submit order
                                </Button>
                                {/* <Button
                size="large"
                // type="primary"
                // disabled={!stripe}
                style={{ marginTop: "2em" }}
                onClick={()=> {props.setCurrentStep(1)}}
              >
                Edit order
              </Button> */}
                            </Space>
                        </div>
                    </Space>
                </LabeledBox>
            )}
        </>
    )
}
